// TOP LEVEL IMPORTS
import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
// ROUTE CONTAINERS
import PublicRoute from 'components/route-components/PublicRoute'; // will redirect them to home screen if signed in
import ProtectedRoute from 'components/route-components/ProtectedRoute'; // will redirect them to login if signed out
import AdminRoute from 'components/route-components/AdminRoute'; 
// LAYOUTS
import BlankLayout from 'components/layout/BlankLayout';
import AdminLayout from 'components/layout/AdminLayout';
// COMPONENTS
import OfflinePlaceholder from 'components/common/OfflinePlaceholder';
import LoadingScreen from 'components/common/LoadingScreen';
import EmptyState from 'components/common/EmptyState';
// ROUTES
import LoginRoute from 'routes/auth-login';
import AdminHomeRoute from 'routes/admin-home';
import AdminUsersRoute from 'routes/admin-users';
import AdminUserDetailRoute from 'routes/admin-user-detail';
import AdminDashboardsRoute from 'routes/admin-dashboards';
import AdminOrganizationsRoute from 'routes/admin-organizations';
import AppDashboardPreviewRoute from 'routes/app-dashboard-preview';
import AppHomeRoute from 'routes/app-home';
import AppContactUsRoute from 'routes/app-contact-us';
import AppTermsRoute from 'routes/app-terms';
import AppPrivacyRoute from 'routes/app-privacy';
import AppAccountRoute from 'routes/app-account';
import AppSubscriptionsRoute from 'routes/app-dashboards';
// import AppAccount from 'routes/app-account';
import PageNotFound from 'routes/not-found';
import PasswordResetRoute from 'routes/auth-password-reset';
import ForgotPasswordRoute from 'routes/auth-forgot-password';
import EnrollAccountRoute from 'routes/auth-enroll-account';
import AppDashboardDetail from 'routes/app-dashboard-detail';
import AdminOrganizationDetailRoute from 'routes/admin-organization-detail';
import AdminEditTeamRoute from 'routes/admin-edit-team';
import AdminDashboardDetailRoute from 'routes/admin-dashboard-detail';
import AdminActivityLogsRoute from 'routes/admin-activity-logs';
// LIB
import styled from 'styled-components';
// APOLLO
import { compose, graphql } from 'react-apollo';
import currentUserQuery from 'ApolloClient/Queries/currentUser';
import DashboardLayout from '../components/layout/DashboardLayout/index';

// CONSTANTS & DESTRUCTURING
// ========================================

// HELPER FUNCTIONS
// ========================================
// for these errors, we don't want to show the error/empty state, we want the user to go to the login screen.
const isWhiteListError = errorMessage => {
  let result = false;
  let WHITE_LISTED_ERRORS = ['Signature verification failed', 'JWTExtendedException', 'Missing JWT'];
  // for each through each error and see if the errorMessage is one of our whiteListErrors...
  // if it's true, isWhiteListError returns true,
  // and the ui will not display an error but rather let user get booted to the login screen
  WHITE_LISTED_ERRORS.forEach(whiteListError => {
    if (errorMessage.includes(whiteListError)) {
      result = true;
    }
  });
  return result;
};

// STYLED-COMPONENTS
// ========================================
const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AppContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const ConnectionIssue = () => (
  <Container>
    <EmptyState
      title="We seem to be having connection issues..."
      subtitle="Don’t worry. This page will automatically reconnect soon."
    />
  </Container>
);

// EXPORTED COMPONENT
// ========================================
class AppRoutes extends React.Component {

  componentDidMount() {
    const { currentUser } = this.props.currentUserQuery;
    window.mixpanel.track("App loaded");
    if (currentUser && currentUser._id && currentUser.emails && currentUser.emails[0]) {
      window.mixpanel.identify(currentUser, { email: currentUser.emails[0].address })
    }
  }

  render() {
    const { loading, error, currentUser } = this.props.currentUserQuery;

    if (loading) {
      return <LoadingScreen theme={this.props.theme} />;
    }

    // show specific message for certain network/timeout errors
    if (
      error
      && error.message
      && (error.message.includes('Timeout exceeded') || error.message.includes('ConnectionTimeout'))
    ) {
      return <ConnectionIssue />;
    }

    // all other errors (that are not in our white listed errors list) just print to screen for now, unless it's a white listed error
    if (error && error.message && !isWhiteListError(error.message)) {
      return (
        <Container>
          <EmptyState title={error.message} />
        </Container>
      );
    }

    return (
      <AppContainer>
        <React.Fragment>
          {/* <OfflinePlaceholder /> */}
          <Router>
            <Switch>
              <PublicRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/recover-password/:token"
                component={PasswordResetRoute}
              />
              <PublicRoute
                layout={BlankLayout}
                exact
                currentUser={currentUser}
                path="/forgot-password"
                component={ForgotPasswordRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/users"
                component={AdminUsersRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/users/:userId"
                component={AdminUserDetailRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/organization/:orgId/edit-team"
                component={AdminEditTeamRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/organization/:orgId"
                component={AdminOrganizationDetailRoute}
              />

              
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/organizations"
                component={AdminOrganizationsRoute}
              />

              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/dashboards/:id"
                component={AdminDashboardDetailRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/dashboards"
                component={AdminDashboardsRoute}
              />

              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/logs"
                component={AdminActivityLogsRoute}
              />
              
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin"
                component={AdminHomeRoute}
              />
              <PublicRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/enroll-account/:token"
                component={EnrollAccountRoute}
              />
              <PublicRoute layout={BlankLayout} currentUser={currentUser} path="/dashboard-preview/:dashboardProductId" component={AppDashboardPreviewRoute} />
              <ProtectedRoute
                layout={DashboardLayout}
                exact
                currentUser={currentUser}
                path="/dashboards/:id/:orgId"
                component={AppDashboardDetail}
              />
              <ProtectedRoute layout={BlankLayout} currentUser={currentUser} path="/dashboards" component={AppSubscriptionsRoute} />
              <ProtectedRoute layout={BlankLayout} currentUser={currentUser} path="/account" component={AppAccountRoute} />
              <PublicRoute exact layout={BlankLayout} currentUser={currentUser} path="/terms" component={AppTermsRoute} />
              <PublicRoute exact layout={BlankLayout} currentUser={currentUser} path="/privacy" component={AppPrivacyRoute} />
              <PublicRoute exact layout={BlankLayout} currentUser={currentUser} path="/contact" component={AppContactUsRoute} />
              <PublicRoute exact layout={BlankLayout} currentUser={currentUser} path="/login" component={LoginRoute} />
              <PublicRoute exact layout={BlankLayout} currentUser={currentUser} path="/" component={LoginRoute} />
              {/* <PublicRoute exact layout={BlankLayout} currentUser={currentUser} path="/" component={AppHomeRoute} /> */}
              <Route
                render={() => (
                  <BlankLayout>
                    <PageNotFound />
                  </BlankLayout>
                )}
              />
            </Switch>
          </Router>
        </React.Fragment>
      </AppContainer>
    );
  }
}

// APOLLO
// ==============================
let GET_USER_QUERY_NAME = 'currentUserQuery';

let options = () => ({
  pollInterval: 300000, // rerun currentUser every 5 minutes
});

// EXPORT
// ==============================
export default compose(graphql(currentUserQuery, { options, name: GET_USER_QUERY_NAME }))(AppRoutes);



// WEBPACK FOOTER //
// ./src/routes/index.js